// frontend/src/components/training/ContentBlock.jsx
//
// Renderiza un bloque de contenido de una lección.
//
// Los embeds SIEMPRE usan la `embed_url` que calculó el backend: aquí no se
// construye ninguna URL de iframe a partir de lo que escribió el creador. Si el
// backend dice `embeddable: false`, se muestra una tarjeta con enlace externo —
// nunca un iframe hacia un dominio arbitrario.

import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, Link, Paper, Stack, Typography,
} from '@mui/material';
import ActivityBlock from './ActivityBlock';
import {
  Description as DocIcon,
  Launch as LaunchIcon,
  PlayCircleOutline as PlayIcon,
} from '@mui/icons-material';

// Contenedor 16:9 que no desborda en móvil.
const marcoVideo = {
  position: 'relative',
  width: '100%',
  paddingTop: '56.25%',
  borderRadius: 2,
  overflow: 'hidden',
  bgcolor: '#000',
  '& iframe': {
    position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0,
  },
};

const TarjetaExterna = ({ url, titulo, descripcion, icono }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icono}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>{titulo}</Typography>
        {descripcion && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {descripcion}
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" endIcon={<LaunchIcon />}
        href={url} target="_blank" rel="noopener noreferrer">
        Abrir
      </Button>
    </Stack>
  </Paper>
);

const ContentBlock = ({ block, checkedItems = [], onToggleChecklist,
  onSubmitActivity, submissions }) => {
  const p = block.payload || {};

  switch (block.block_type) {
    case 'heading':
      return (
        <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
          {p.text || block.title}
        </Typography>
      );

    case 'text':
    case 'rich_text':
      return (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {p.text || ''}
        </Typography>
      );

    case 'video':
      // `embeddable` lo decide el backend según su lista blanca de proveedores.
      if (p.embeddable && p.embed_url) {
        return (
          <Box>
            {block.title && (
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{block.title}</Typography>
            )}
            <Box sx={marcoVideo}>
              <iframe
                src={p.embed_url}
                title={block.title || 'Vídeo de la lección'}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </Box>
            {p.provider && (
              <Chip size="small" variant="outlined" label={p.provider} sx={{ mt: 1, height: 20 }} />
            )}
          </Box>
        );
      }
      return (
        <TarjetaExterna
          url={p.url}
          titulo={block.title || 'Ver vídeo'}
          descripcion="Este proveedor no permite reproducir aquí; se abre en una pestaña nueva."
          icono={<PlayIcon color="primary" />}
        />
      );

    case 'embed':
      return (
        <Box sx={marcoVideo}>
          <iframe src={p.embed_url} title={block.title || 'Contenido incrustado'}
            loading="lazy" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
        </Box>
      );

    case 'image':
      if (!p.url) return null;
      return (
        <Box>
          <Box
            component="img"
            src={p.url}
            alt={p.caption || block.title || ''}
            loading="lazy"
            sx={{
              width: '100%', maxWidth: p.display === 'thumbnail' ? 320 : '100%',
              height: 'auto', borderRadius: 2, display: 'block',
              objectFit: 'contain', bgcolor: 'action.hover',
            }}
            // Si la URL externa cae, no debe quedar el icono roto del navegador.
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {p.caption && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {p.caption}
            </Typography>
          )}
        </Box>
      );

    case 'file':
    case 'document':
      return (
        <TarjetaExterna
          url={p.url}
          titulo={block.title || p.filename || 'Documento'}
          descripcion={p.size_label}
          icono={<DocIcon color="primary" />}
        />
      );

    case 'link':
    case 'cta':
      if (p.behavior === 'button' || block.block_type === 'cta') {
        return (
          <Button variant="contained" endIcon={<LaunchIcon />}
            href={p.url} target="_blank" rel="noopener noreferrer">
            {block.title || 'Abrir enlace'}
          </Button>
        );
      }
      return (
        <TarjetaExterna url={p.url} titulo={block.title || p.url}
          descripcion={p.description} icono={<LaunchIcon color="primary" />} />
      );

    case 'checklist':
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {block.title && (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{block.title}</Typography>
          )}
          <Stack>
            {(p.items || []).map((item) => (
              <FormControlLabel
                key={item.key}
                control={
                  <Checkbox
                    checked={checkedItems.includes(item.key)}
                    onChange={(e) => onToggleChecklist?.(block.id, item.key, e.target.checked)}
                  />
                }
                label={item.label}
              />
            ))}
          </Stack>
        </Paper>
      );

    case 'callout':
      return <Alert severity={p.severity || 'info'}>{p.text || block.title}</Alert>;

    case 'confirmation':
    case 'task':
    case 'question':
    case 'quiz':
      // La interacción vive en ActivityBlock: aquí solo se decide el tipo.
      return (
        <ActivityBlock block={block} onSubmit={onSubmitActivity}
          submissions={submissions?.[block.id]} />
      );

    case 'divider':
      return <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 1 }} />;

    default:
      // Un tipo desconocido no puede romper la lección entera.
      return null;
  }
};

ContentBlock.propTypes = {
  block: PropTypes.object.isRequired,
  checkedItems: PropTypes.array,
  onToggleChecklist: PropTypes.func,
  onSubmitActivity: PropTypes.func,
  submissions: PropTypes.object,
};

export default ContentBlock;
